import { CSSProperties } from "react";
import { useDragLayer } from "react-dnd";
import { observer } from "mobx-react-lite";
import DesignBlock from "../shared/design-block";
import { IContentBlock, IDesignBlock } from "../../../types/iblock";
import { renderContentBlock } from "./render-content-block";
import { IButtonBlockData, IColumnsBlockData, IImageBlockData, IHeroSliderBlockData } from "../../../types/content-block-data";
import blocksManager from "../../../stores/blocks-manager";
import contentManager from "../../../stores/content-manager";

/**
 * CustomDragPreview – Floating Drag Preview for DesignCanvas Blocks
 *
 * @component CustomDragPreview
 * @description
 * Renders a floating, dynamically-sized preview of a DesignBlock while it is being
 * dragged within the DesignCanvas (or between columns/canvas), using react-dnd's custom drag layer.
 * Follows the user’s cursor and displays the block as it will appear when dropped.
 *
 * @features
 * - Shows real-time, WYSIWYG preview of the currently dragged block
 * - Adapts preview width and style based on context and drag location
 * - Works for blocks dragged from both the main canvas and within columns
 * - Ignores pointer events to prevent accidental interactions during drag
 * - Matches the actual block’s rendering as closely as possible, including shadow and border
 *
 * @remarks
 * - Internal-use only for the PageBuilder drag-and-drop flow; not a reusable component
 * - Depends on react-dnd for drag state and context
 * - Hides itself when not dragging, or when not on a DESIGN_BLOCKS drag item
 *
 * @example
 * <CustomDragPreview />
 *
 * @since 2025-06-12
 * @lastUpdated 2025-07-15
 * @author
 * d2d-weizhi (Chen Weizhi)
 */
export default observer(function CustomDragPreview() {
  const {
    item,
    itemType,
    isDragging,
    currentOffset,
  } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getClientOffset(),
  }));

  if (!isDragging || !currentOffset || itemType !== "DESIGN_BLOCKS") return null;

  let block: IDesignBlock | undefined = undefined;

  if (item.context === "canvas") {
    block = contentManager.getDesignContent().find(
      (blk: IDesignBlock) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === item.designBlockId
    );
  } else if (item.context === "columns") {
    const columnsBlock = contentManager.getDesignContent().find(
      (blk: IDesignBlock) => blk.contentBlock.blockType === "columns"
    );

    if (columnsBlock) {
      const columnsData = columnsBlock.contentBlock.blockData as IColumnsBlockData;
      for (const col of columnsData.children) {
        const match = col.find((child: IDesignBlock) =>
          blocksManager.getDesignTree().get(child.contentBlock.contentBlockId)?.designBlockId! === item.designBlockId);
        if (match) {
          block = match;
          break;
        }
      }
    }
  }

  function getBlockPreviewStyle(contentBlock: IContentBlock): { width: number, height?: number, maxWidth?: number, minWidth?: number } {
    switch (contentBlock.blockType.toLowerCase()) {
      case "image": {
        // Defaults
        const baseWidth = 700;
        const scale = 0.60;

        const imageData = (contentBlock.blockData as IImageBlockData);

        // Try to get user-set width/height (from blockData)
        let imgWidth = Number(imageData?.width) || baseWidth;
        let imgHeight = Number(imageData?.height) || undefined;

        // If width is 100% (string), set to baseWidth
        if (typeof imageData?.width?.value === "string" && imageData.width!.unit === "%") {
          imgWidth = baseWidth;
        }
        // If height is auto (string), set a safe default aspect ratio
        if ((imageData?.height?.value === "" || imageData?.height?.value === "0") && imageData?.height?.unit === "px") {
          // Use a default aspect ratio
          imgHeight = Math.round(imgWidth * (9 / 16));
        }

        return {
          width: Math.round(imgWidth * scale),
          height: Math.round(Number(imgHeight) * scale),
          maxWidth: Math.round(imgWidth * scale),
          minWidth: 200,
        };
      }
      case "video": {
        const baseWidth = 700;
        const scale = 0.60;
        const aspect = 9 / 16; // 16:9
        const vidWidth = baseWidth;
        const vidHeight = Math.round(vidWidth * aspect);

        return {
          width: Math.round(vidWidth * scale),
          height: Math.round(vidHeight * scale),
          maxWidth: Math.round(vidWidth * scale),
          minWidth: 300,
        };
      }
      case "hero_slider": {
        const baseWidth = 700;
        const scale = 0.80;
        const heroData = (contentBlock.blockData as IHeroSliderBlockData) ?? {};

        // Use user-set height, fallback to 400
        let heroHeight = Number(heroData?.height) || 400;

        // If user passed height as string (just in case)
        if ((heroData?.height?.value === "" || heroData?.height?.value === "0") && heroData.height.unit.trim() !== "") {
          heroHeight = Number(heroData.height) || 400;
        }

        return {
          width: 1200 * scale,
          height: Math.round(heroHeight * (heroHeight / 1200)),
        };
      }
      case "button": {
        let width = 150; // sensible default
        let height = 48; // sensible default

        const blockRef = block?.contentBlock?.contentBlockRef as React.RefObject<HTMLButtonElement>;
        const buttonData = block?.contentBlock.blockData as IButtonBlockData;

        if (blockRef && blockRef.current) {
          const rect = blockRef.current.getBoundingClientRect();
          width = rect.width;
          height = rect.height;
        }

        return { width: parseInt(buttonData.width?.value!) + 32, height: height + 8 };
      }
      case "columns": {
        return { width: 1000, maxWidth: 1000 };
      }
      // Add other special cases if needed
      default:
        return { width: 400, maxWidth: 400 };
    }
  }

  if (!block) return null;

  const previewContent = renderContentBlock(block);

  // Dynamic Preview Width based on context
  const scaleFactor = (item.WidthScale ?? 1) - 0.3;
  const baseMaxWidth = 500;
  const previewWidth = Math.min(baseMaxWidth * scaleFactor, baseMaxWidth);
  const { width, height, maxWidth, minWidth } = getBlockPreviewStyle(block.contentBlock);

  const style: CSSProperties = {
    position: "fixed",
    borderLeftWidth: "4px",
    borderRightWidth: "0px",
    borderColor: "white",
    pointerEvents: "none",
    top: 0,
    left: 50,
    backgroundColor: "transparent",
    transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)`,
    zIndex: 100,
    width: `${width}px`,
    ...(height && { height: `${height}px` }),
    ...(maxWidth && { maxWidth: `${maxWidth}px` }),
    ...(minWidth && { minWidth: `${minWidth}px` }),
    cursor: "grabbing",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)", // 💡 shadow-xl style
  };
  
  const treeEntry = blocksManager.getDesignTree().get(block.contentBlock.contentBlockId);
  const designBlockId = treeEntry?.designBlockId ?? "preview-block";

  return (
    <div style={style} className="mx-auto rounded-md"> {/* optional centering */}
      <DesignBlock
        contentBlock={block.contentBlock}
        designBlockId={designBlockId}
        designBlockRef={undefined} // Not needed for preview
        blockContext={item.context}
        isDragging={true}
        isDragPreview={true}
        isEditing={false}
        onDelete={undefined}
      >
        {previewContent}
      </DesignBlock>
    </div>
  );
});
